import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ArrowLeft, Flame, Target, Layers, TrendingUp, CheckCircle2, BarChart3,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { ProBadge } from "@/components/AskAI";
import { Button } from "@/components/ui/button";

function Stat({ icon: Icon, label, value, sub, color }) {
  return (
    <div
      className="rounded-2xl border border-border bg-card shadow-brutal-sm p-5"
      data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <span className={`grid place-items-center h-10 w-10 rounded-xl ${color}`}><Icon className="h-5 w-5" /></span>
      <p className="mt-3 font-heading font-extrabold tracking-tight text-3xl leading-none">{value}</p>
      <p className="mt-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export default function Analytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = () => {
    setErr(false);
    api.get("/pro/analytics").then(({ data }) => setData(data)).catch(() => setErr(true));
  };

  useEffect(() => { window.scrollTo(0, 0); load(); }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <PageTransition className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
          <div className="rounded-2xl border border-border bg-card shadow-brutal-sm p-10 text-center" data-testid="analytics-error">
            <p className="font-heading font-bold text-lg">Couldn't load your analytics.</p>
            <Button onClick={load} className="mt-4 bg-primary text-primary-foreground">Try again</Button>
          </div>
        </PageTransition>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const s = data.summary;
  const maxAct = Math.max(1, ...data.activity.map((a) => a.completed));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageTransition className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
        <button onClick={() => navigate("/dashboard")} data-testid="analytics-back"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 rounded-full px-3 py-1.5">Advanced analytics</span>
          <ProBadge />
        </div>
        <h1 className="mt-4 font-heading font-extrabold tracking-tight text-3xl md:text-4xl">Your learning progress</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">A deeper look at how far you've come — completion, accuracy, momentum and where to push next.</p>

        {/* summary stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-summary">
          <Stat icon={Target} label="Lessons done" value={`${s.completed}/${s.total}`} sub={`${s.percent}% of your path`} color="bg-primary/10 text-primary" />
          <Stat icon={Flame} label="Day streak" value={s.streak} sub={s.streak > 0 ? "Keep it alive!" : "Start one today"} color="bg-brand-tangerine/15 text-brand-tangerine" />
          <Stat icon={CheckCircle2} label="Avg quiz score" value={s.avg_quiz != null ? `${s.avg_quiz}%` : "—"} sub={s.avg_quiz != null ? "Across completed lessons" : "Take a quiz to see this"} color="bg-success/15 text-success" />
          <Stat icon={Layers} label="Topics covered" value={s.topics_covered} sub={`Highest level reached: ${s.level_reached || "—"}`} color="bg-brand-cyber/10 text-brand-cyber" />
        </div>

        {/* progress by level */}
        {data.by_level.length > 0 && (
          <div className="mt-6 rounded-2xl border border-border bg-card shadow-brutal-sm p-6 md:p-7" data-testid="analytics-by-level">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center h-8 w-8 rounded-lg bg-primary text-primary-foreground"><TrendingUp className="h-4 w-4" /></span>
              <h2 className="font-heading font-extrabold tracking-tight text-xl">Progress by level</h2>
            </div>
            <div className="mt-5 space-y-4">
              {data.by_level.map((lv) => {
                const pct = lv.total ? Math.round((lv.done / lv.total) * 100) : 0;
                return (
                  <div key={lv.level} data-testid={`level-row-${lv.level}`}>
                    <div className="flex items-center justify-between text-sm font-semibold mb-1.5">
                      <span>{lv.label || `Level ${lv.level}`}</span>
                      <span className="text-muted-foreground font-mono text-xs">{lv.done}/{lv.total}</span>
                    </div>
                    <div className="h-3 rounded-full bg-secondary overflow-hidden">
                      <motion.div className="h-full rounded-full bg-primary"
                        initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 14-day activity */}
        <div className="mt-6 rounded-2xl border border-border bg-card shadow-brutal-sm p-6 md:p-7" data-testid="analytics-activity">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-8 w-8 rounded-lg bg-brand-cyber text-white"><BarChart3 className="h-4 w-4" /></span>
            <h2 className="font-heading font-extrabold tracking-tight text-xl">Last 14 days</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Lessons you completed each day.</p>
          <div className="mt-5 h-56 w-full">
            <ResponsiveContainer width="99%" height={224} minHeight={224}>
              <BarChart data={data.activity} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} interval={1} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--secondary))" }}
                  contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 13 }}
                  labelStyle={{ fontWeight: 700 }}
                />
                <Bar dataKey="completed" radius={[6, 6, 0, 0]} maxBarSize={26}>
                  {data.activity.map((a, i) => (
                    <Cell key={i} fill={a.completed >= maxAct && a.completed > 0 ? "#0055FF" : "#7C3AED"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <Button onClick={() => navigate("/dashboard")} variant="outline" data-testid="analytics-cta">
            Back to your roadmap
          </Button>
        </div>
      </PageTransition>
    </div>
  );
}
